import { api } from "./axios";

export const adminApi = {
  getUsers: ({
    page = 1,
    limit = 10,
    search = "",
    searchType = "name",
    type,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    searchType?: string;
    type?: string;
  } = {}) =>
    api.get(`/admin/get-all-users`, {
      params: {
        page,
        limit,
        search,
        searchType,
        // Only sent when provided, so existing callers are unaffected.
        ...(type ? { type } : {}),
      },
    }),

  inviteUser: (data: { email: string; label: string }) =>
    api.post("/admin/invite-user", data),

  inviteSubscription: (data: {
    email: string;
    label: string;
    packageId: string;
  }) => api.post("/admin/invite-user-subscription", data),

  createPitch: (data: { title: string; description: string }) =>
    api.post("/admin/pitches", data),

  deleteUser: (userId: number) => api.post(`/admin/remove-user`, { userId }),

  promoteUser: (userId: number) => api.post(`/admin/promote-user`, { userId }),

  getAllCreditsForUser: ({
    userId,
    page,
    limit,
  }: {
    userId: number;
    page?: number;
    limit?: number;
  }) =>
    api.get(
      `/admin/get-user-credits/${userId}?page=${page || 1}&limit=${limit || 10}`
    ),

  getAllPromoUsers: ({
    page = 1,
    limit = 10,
    search = "",
    searchType = "email",
  }) =>
    api.get(
      `/admin/get-all-promo-users?page=${page}&limit=${limit}&search=${search}&searchType=${searchType}`
    ),

  addLabel: (data: {
    name: string;
    type?: string;
    availableDSPs: string[];
    availableAnalyticsDSPs: string[];
  }) => api.post("/admin/add-label", data),

  getLabels: () => api.get("/admin/get-labels"),

  updateLabel: (
    id: number,
    {
      name,
      type,
      availableDSPs,
      availableAnalyticsDSPs,
    }: {
      name: string;
      type: string;
      availableDSPs: string[];
      availableAnalyticsDSPs: string[];
    }
  ) =>
    api.put(`/admin/update-label/${id}`, {
      name,
      type,
      availableDSPs,
      availableAnalyticsDSPs,
    }),

  createInviteCode: (data: { label: string }) =>
    api.post("/admin/create-invite-code", data),

  getInviteCodes: ({
    page = 1,
    limit = 10,
    search = "",
    searchType = "code",
  }) =>
    api.get("/admin/get-invite-codes", {
      params: {
        page,
        limit,
        search,
        searchType,
      },
    }),

  getAllCustomPackages: () => api.get("/admin/get-all-custom-packages"),

  validateToken: (token: string) =>
    api.get("/subscription/validate-token", {
      params: { token },
    }),

  getPackageDetails: (packageId: string) =>
    api.get("/subscription/get-package-details", {
      params: { packageId },
    }),

  getCheckoutUrl: (
    packageId: string,
    email: string,
    userId: string,
    token?: string
  ) =>
    api.get("/subscription/get-checkout-url", {
      params: { packageId, email, userId, token },
    }),
  addAppUpdate: (data: any) => api.post("/admin/add-app-update", data),
  draftAppUpdate: (data: any) => api.post("/admin/draft-app-update", data),
  getAllAppUpdates: ({
    page = 1,
    limit = 10,
    search = "",
    searchType = "",
    userRole = "",
    includeDismissed = false,
    addPagination = true,
  } = {}) =>
    api.get("/admin/get-all-app-updates", {
      params: {
        page,
        limit,
        search,
        searchType,
        userRole,
        includeDismissed,
        addPagination,
      },
    }),
  updateAppUpdate: (id: number, data: any) =>
    api.put(`/admin/update-app-update/${id}`, data),
  deleteAppUpdate: (id: number) => api.delete(`/admin/delete-app-update/${id}`),
  viewAppUpdate: (id: number, userId: number) =>
    api.get(`/admin/view-app-update/${id}/${userId}`),
  getAppUpdateAnalytics: (params?: { page?: number; limit?: number; filter?: string; sort?: string; sortDirection?: string }) =>
    api.get("/admin/get-app-update-analytics", { params }),

  // Admin Analytics APIs
  getAllAccountDetails: () => api.get("/admin/get-all-account-details"),
  getAllDspSetUpAccounts: () => api.get("/admin/get-all-dsp-set-up-accounts"),
  getTimingMetrics: () => api.get("/admin/get-timing-matrics"),
  getAllUserTracking: ({
    label,
    sort = "asc",
    page = 1,
    limit = 10,
    search = "",
  }: {
    label?: number;
    sort?: "asc" | "desc";
    page?: number;
    limit?: number;
    search?: string;
  }) =>
    api.get("/admin/get-all-user-tracking", {
      params: {
        label,
        sort,
        page,
        limit,
        search,
      },
    }),
  // DSP Organizations
  getDspOrganizations: () => api.get("/admin/get-all-dsp-organizations"),

  createDspOrganization: (data: { name: string; labelIds: number[] }) =>
    api.post("/admin/create-dsp-organization", data),

  updateDspOrganization: (
    id: string,
    data: {
      name: string;
      labelIds: number[];
    }
  ) => api.put(`/admin/update-dsp-organization/${id}`, data),

  inviteDspOrganizationUser: (data: {
    email: string;
    organizationId: string;
    packageId: string;
  }) => api.post("/admin/invite-user-subscription", data),

  deleteDspOrganization: (id: string) =>
    api.delete(`/admin/delete-dsp-organization/${id}`),

  getDspUsers: (
    organizationId: string,
    params: {
      page?: number;
      limit?: number;
    } = {}
  ) =>
    api.get(`/admin/get-dsp-users/${organizationId}`, {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
      },
    }),

  // DSP User - Get all pitches
  getDspPitches: (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      searchType?: string;
      mainGenre?: string;
      companyName?: string;
      status?: string;
      platform?: string;
      starred?: boolean;
    } = {}
  ) => {
    // Only include non-empty parameters
    const cleanParams: any = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    if (params.search) {
      cleanParams.search = params.search;
      if (params.searchType) {
        cleanParams.searchType = params.searchType;
      }
    }

    if (params.mainGenre) {
      cleanParams.mainGenre = params.mainGenre;
    }

    if (params.companyName) {
      cleanParams.companyName = params.companyName;
    }

    if (params.status) {
      cleanParams.status = params.status;
    }

    if (params.platform) {
      cleanParams.platform = params.platform;
    }

    if (params.starred) {
      cleanParams.starred = true;
    }

    return api.get("/dsp-user/get-all-pitches", {
      params: cleanParams,
    });
  },

  // DSP User - Toggle starred pitch
  toggleStarredPitch: (pitchId: number) =>
    api.put(`/dsp-user/toggle-starred-pitch/${pitchId}`),

  // DSP User - Get a single artist by id
  getDspArtist: (id: string | number) =>
    api.get(`/dsp-user/get-artist/${id}`),

  // DSP User - Get artists associated with the DSP user's organization
  getDspAssociatedArtists: ({
    page = 1,
    limit = 10,
    search = "",
  }: {
    page?: number;
    limit?: number;
    search?: string;
  } = {}) => {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    return api.get("/dsp-user/get-dsp-associated-artists", { params });
  },

  // DSP User - Get all starred pitches
  getStarredDspPitches: (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      searchType?: string;
      mainGenre?: string;
      companyName?: string;
      status?: string;
      platform?: string;
    } = {}
  ) => {
    // Only include non-empty parameters
    const cleanParams: any = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    if (params.search) {
      cleanParams.search = params.search;
      if (params.searchType) {
        cleanParams.searchType = params.searchType;
      }
    }

    if (params.mainGenre) {
      cleanParams.mainGenre = params.mainGenre;
    }

    if (params.companyName) {
      cleanParams.companyName = params.companyName;
    }

    if (params.status) {
      cleanParams.status = params.status;
    }

    if (params.platform) {
      cleanParams.platform = params.platform;
    }

    return api.get("/dsp-user/get-all-starred-pitches", {
      params: cleanParams,
    });
  },

  // SoundChart API Tracking
  trackSoundChartApi: (data: {
    api_call: string;
    date: string;
    time: string;
    email: string;
    labelName: string;
    isSucceeded: boolean;
  }) => api.post("/track-soundchart-api", data),

  getAllSoundChartTracking: (params: {
    months?: number;
    includeData?: boolean;
    page?: number;
    limit?: number;
  } = {}) =>
    api.get("/get-all-soundchart-tracking", {
      params: {
        months: params.months || 3,
        includeData: params.includeData ?? false,
        page: params.page || 1,
        limit: params.limit || 100,
      },
    }),

  // AIMS API Tracking
  trackAimsApi: (data: {
    api_call: string;
    date: string;
    time: string;
    email: string;
    labelName: string;
    isSucceeded: boolean;
  }) => api.post("/aims/track-api", data),

  getAllAimsTracking: (params: {
    months?: number;
    page?: number;
    limit?: number;
  } = {}) =>
    api.get("/aims/tracking", {
      params: {
        months: params.months || 3,
        page: params.page || 1,
        limit: params.limit || 100,
      },
    }),
  impersonateUser: (userId: number) => api.post(`/admin/impersonate-user/${userId}`),

  getAllSubscribedUsers: ({
    page = 1,
    limit = 20,
    search = "",
    searchType = "email",
  }: {
    page?: number;
    limit?: number;
    search?: string;
    searchType?: "email" | "status";
  } = {}) =>
    api.get("/admin/get-subscribed-users", {
      params: { page, limit, search, searchType },
    }),

  getAllArtists: ({
    page = 1,
    limit = 10,
    search = "",
    company = "",
  }: {
    page?: number;
    limit?: number;
    search?: string;
    company?: string;
  } = {}) => {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    if (company) params.company = company;
    return api.get("/admin/get-all-artists", { params });
  },

  /** This month's SoundCharts usage against the monthly quota (AD-305). */
  getSoundchartsQuota: () => api.get("/admin/soundcharts-quota"),

  /** Whether playlist views read stored data or the live SoundCharts fetch (AD-305). */
  getPlaylistSettings: () => api.get("/admin/playlist-settings"),
  updatePlaylistSettings: (settings: { readFromDb: boolean }) =>
    api.put("/admin/playlist-settings", settings),

  /** Playlist sync settings (backfill, cadence, stale threshold) and the backfill estimate (AD-305). */
  getPlaylistSyncSettings: () => api.get("/admin/playlist-sync-settings"),
  updatePlaylistSyncSettings: (settings: PlaylistSyncSettingsUpdate) =>
    api.put("/admin/playlist-sync-settings", settings),
};

export type SyncCadence = "daily" | "weekly" | "fortnightly" | "off";

export interface PlaylistSyncSettingsUpdate {
  backfill?: { enabled?: boolean; dailyCallBudget?: number; recordsPerTick?: number };
  cadence?: { pitch?: SyncCadence; roster?: SyncCadence };
  staleAfterDays?: number;
}

export interface BackfillEstimate {
  records: number;
  withoutIsrc: number;
  pulledSoFar: number;
  callsPerRecord: number;
  callsPerRecordMeasured: boolean;
  sampledRecords: number;
  estimatedCalls: number;
  monthlyQuota: number;
  usedThisMonth: number;
  remainingQuota: number;
  fitsInRemainingQuota: boolean;
  dailyCallBudget: number;
  estimatedDays: number;
  enabled: boolean;
}

export interface PlaylistSyncSettings {
  syncEnabled: boolean;
  backfill: { enabled: boolean; dailyCallBudget: number; recordsPerTick: number };
  cadence: { pitch: SyncCadence; roster: SyncCadence };
  staleAfterDays: number;
  estimate: BackfillEstimate;
}
